
import React, { useState, useEffect } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { X, Mail, Smartphone, Command, ArrowRight, Sparkles, Quote, Loader2, Gift } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { sendVerificationCode, sendSmsCode, register, registerWithPhone } from '../services/auth';

interface SignUpModalProps {
    onClose: () => void;
    onSignUp: () => void;
    onSwitchToLogin: () => void;
    initialInviteCode?: string;
    prefill?: { method: 'email' | 'phone'; contact: string; code: string } | null;
}

export default function SignUpModal({ onClose, onSignUp, onSwitchToLogin, initialInviteCode = '', prefill }: SignUpModalProps) {
    const [method, setMethod] = useState<'email' | 'phone'>(prefill?.method || 'email');
    const [testimonialIdx, setTestimonialIdx] = useState(0);
    const { t, language } = useLanguage();

    const isFromLogin = !!prefill;
    const lockCode = isFromLogin && !!prefill?.code;

    // 表单状态
    const [fullName, setFullName] = useState('');
    const [emailOrPhone, setEmailOrPhone] = useState(prefill?.contact || '');
    const [verificationCode, setVerificationCode] = useState(prefill?.code || '');
    const [password, setPassword] = useState('');
    const [inviteCode, setInviteCode] = useState(initialInviteCode);

    // 验证码发送状态
    const [sendingCode, setSendingCode] = useState(false);
    const [countdown, startCountdown] = useCountdown(60);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const testimonials = t.auth.testimonials;

    // 发送验证码
    const handleSendCode = async () => {
        if (!emailOrPhone.trim()) {
            setError(method === 'email' ? '请输入邮箱' : '请输入手机号');
            return;
        }
        if (countdown > 0 || sendingCode) return;

        setSendingCode(true);
        setError('');
        try {
            if (method === 'email') {
                await sendVerificationCode(emailOrPhone);
            } else {
                await sendSmsCode(emailOrPhone, 'register');
            }
            startCountdown();
        } catch (err: any) {
            setError(err.message || '发送验证码失败');
        } finally {
            setSendingCode(false);
        }
    };

    // 提交注册
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!fullName.trim() || !emailOrPhone.trim() || !verificationCode.trim() || !password.trim()) {
            setError('请填写所有必填项');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            if (method === 'email') {
                await register({
                    email: emailOrPhone,
                    password,
                    username: fullName,
                    verificationCode,
                    inviteCode: inviteCode.trim() || undefined,
                });
            } else {
                await registerWithPhone({
                    phone: emailOrPhone,
                    code: verificationCode,
                    password,
                    username: fullName,
                    inviteCode: inviteCode.trim() || undefined,
                });
            }
            onSignUp();
        } catch (err: any) {
            setError(err.message || '注册失败');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setTestimonialIdx((prev) => (prev + 1) % testimonials.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
            {/* Backdrop - Stabilized rendering */}
            <div
                className="absolute inset-0 bg-brand-900/10 backdrop-blur-md transform-gpu transition-opacity duration-300 pointer-events-auto"
                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                onClick={onClose}
            ></div>

            <div className="relative w-full max-w-5xl bg-white rounded-modal shadow-modal overflow-hidden flex flex-col md:flex-row h-auto max-h-[95vh] animate-in fade-in zoom-in-95 duration-500 border border-white z-10">

                {/* Left Side: Soft Value & Testimonial Panel */}
                <div className="w-full md:w-[45%] bg-surface-light p-10 md:p-14 flex flex-col relative overflow-hidden shrink-0 border-r border-surface-border">
                    <div className="absolute top-[-50px] left-[-50px] w-80 h-80 bg-brand-200/30 rounded-full blur-[80px]"></div>
                    <div className="absolute bottom-[-50px] right-[-50px] w-80 h-80 bg-prestige-300/20 rounded-full blur-[80px]"></div>
                    <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

                    <div className="relative z-10 mb-auto">
                        <div className="w-12 h-12 bg-brand-900 rounded-xl flex items-center justify-center text-white shadow-lg mb-6">
                            <Command size={24} strokeWidth={2.5} />
                        </div>
                        <h3 className="text-xl font-bold text-brand-900 tracking-tight">Minutes AI</h3>
                    </div>

                    <div className="relative z-10 flex-1 flex flex-col justify-center">
                        <div className="relative min-h-[320px]">
                            {testimonials.map((testimonial, idx) => (
                                <div
                                    key={idx}
                                    className={`absolute inset-0 transition-all duration-1000 ease-in-out flex flex-col justify-center text-center
                                ${idx === testimonialIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
                                >
                                    <p className="text-2xl md:text-3xl font-serif font-bold text-brand-900 leading-snug mb-10 px-4">
                                        “{testimonial.text}”
                                    </p>

                                    <div className="flex flex-col items-center">
                                        <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden mb-4">
                                            <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="text-base font-bold text-brand-900">{testimonial.name}</div>
                                            <div className="text-xs font-medium text-ink-secondary opacity-70 uppercase tracking-widest">{testimonial.role}</div>
                                            <div className="text-xs font-bold text-brand-700">{testimonial.company}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative z-10 mt-auto pt-8">
                        <div className="flex justify-center gap-2">
                            {testimonials.map((_, idx) => (
                                <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === testimonialIdx ? 'w-8 bg-brand-900' : 'w-2 bg-surface-border'}`}></div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="flex-1 p-10 md:p-14 bg-white flex flex-col justify-center relative overflow-y-auto">
                    <button onClick={onClose} className="absolute top-8 right-8 p-2 text-gray-300 hover:text-brand-900 rounded-full hover:bg-gray-50 transition-all z-20">
                        <X size={20} />
                    </button>

                    <div className="mb-10 text-center md:text-left">
                        <h2 className="text-3xl font-serif font-bold text-brand-900 mb-2">{t.auth.createAccount}</h2>
                        <p className="text-gray-400 text-sm font-medium">{t.auth.createSub}</p>
                    </div>

                    <div className="flex p-1.5 bg-surface rounded-2xl mb-8 border border-surface-border">
                        <button onClick={() => !isFromLogin && setMethod('email')} className={`flex-1 py-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${method === 'email' ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border' : 'text-gray-400 hover:text-gray-600'} ${isFromLogin ? 'cursor-not-allowed' : ''}`}>
                            <Mail size={14} /> {t.auth.email}
                        </button>
                        <button onClick={() => !isFromLogin && setMethod('phone')} className={`flex-1 py-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${method === 'phone' ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border' : 'text-gray-400 hover:text-gray-600'} ${isFromLogin ? 'cursor-not-allowed' : ''}`}>
                            <Smartphone size={14} /> {t.auth.phone}
                        </button>
                    </div>

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-5">
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t.auth.fullName}</label>
                                <input
                                    type="text"
                                    placeholder=""
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{method === 'email' ? 'Work Email' : t.auth.mobileNumber}</label>
                                <input
                                    type={method === 'email' ? 'email' : 'tel'}
                                    placeholder=""
                                    value={emailOrPhone}
                                    onChange={(e) => !isFromLogin && setEmailOrPhone(e.target.value)}
                                    readOnly={isFromLogin}
                                    className={`w-full h-12 px-4 rounded-xl border border-surface-border text-sm transition-all outline-none ${isFromLogin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-surface focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5'}`}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t.auth.verificationCode}</label>
                                    <input
                                        type="text"
                                        placeholder=""
                                        value={verificationCode}
                                        onChange={(e) => !lockCode && setVerificationCode(e.target.value)}
                                        readOnly={lockCode}
                                        className={`w-full h-12 px-4 rounded-xl border border-surface-border text-sm text-center font-mono tracking-widest transition-all outline-none ${lockCode ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-surface focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5'}`}
                                    />
                                </div>
                                {!lockCode && (
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={handleSendCode}
                                        disabled={sendingCode || countdown > 0}
                                        className="w-full h-12 rounded-xl text-xs font-bold bg-white text-brand-900 border border-surface-border hover:bg-surface transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {sendingCode ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : countdown > 0 ? (
                                            `${countdown}s`
                                        ) : (
                                            t.auth.sendCode
                                        )}
                                    </button>
                                </div>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t.auth.password}</label>
                                <input
                                    type="password"
                                    placeholder=""
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                                />
                            </div>

                            {/* 邀请码输入框 */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                                    <Gift size={10} className="text-orange-400" />
                                    {language === 'zh' ? '邀请码（选填）' : 'Invite Code (Optional)'}
                                </label>
                                <input
                                    type="text"
                                    placeholder=""
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-orange-50 text-sm focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 transition-all outline-none font-mono tracking-wider"
                                />
                                {inviteCode && (
                                    <p className="text-[10px] text-orange-600 ml-1">
                                        {language === 'zh' ? '使用邀请码注册，双方各得60分钟时长！' : 'Both you and your friend get 60 min!'}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full relative h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                                {submitting ? (
                                    <Loader2 size={18} className="animate-spin relative z-10" />
                                ) : (
                                    <>
                                        <span className="relative z-10">{t.auth.signup}</span>
                                        <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-gray-400 text-sm font-medium">
                            {t.auth.hasAccount}{' '}
                            <button onClick={onSwitchToLogin} className="text-brand-900 font-bold hover:underline ml-1">
                                {t.auth.login}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
